/**
 * timeAwareness.js - Time & Pattern Awareness Service
 * Baba AI - Tracks time patterns, session duration, interaction timing
 * 
 * Features:
 * - Time of day context (morning/afternoon/evening/night)
 * - Session duration tracking
 * - Time between messages
 * - Day/date awareness
 * - ADHD pattern foundation (activation times, session lengths)
 * 
 * Privacy: ALL data stays LOCAL (localStorage only)
 */

class TimeAwareness {
  constructor() {
    this.sessionStart = Date.now();
    this.lastMessageTime = null;
    this.messageCount = 0;
    this.storageKey = 'baba-time-patterns';
    
    // Load historical patterns
    this.patterns = this.loadPatterns();
    
    // Record session start
    this.recordSessionStart();
  }

  // ==========================================
  // CURRENT TIME CONTEXT
  // ==========================================

  /**
   * Get current time context for AI prompt injection
   */
  getCurrentContext() {
    const now = new Date();
    const hour = now.getHours();
    const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' });
    const date = now.toLocaleDateString('nl-BE', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });

    return {
      timeOfDay: this.getTimeOfDay(hour),
      greeting: this.getAppropriateGreeting(hour),
      hour: hour,
      dayOfWeek: dayOfWeek,
      date: date,
      isWeekend: this.isWeekend(now),
      sessionDuration: this.getSessionDuration(),
      timeSinceLastMessage: this.getTimeSinceLastMessage(),
      messageCount: this.messageCount,
      formatted: this.formatContextForPrompt(now)
    };
  }

  /**
   * Get time of day category
   */
  getTimeOfDay(hour) {
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'night';
  }

  /**
   * Get appropriate greeting based on time
   */
  getAppropriateGreeting(hour) {
    if (hour >= 5 && hour < 12) return 'Goedemorgen';
    if (hour >= 12 && hour < 18) return 'Goedemiddag';
    if (hour >= 18 && hour < 23) return 'Goedenavond';
    return 'Hallo'; // Late night - neutral
  }

  /**
   * Check if weekend
   */
  isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  /**
   * Format context for AI prompt injection
   * NOTE: Does NOT call getCurrentContext to avoid circular dependency
   */
  formatContextForPrompt(now) {
    const hour = now.getHours();
    const minutes = now.getMinutes();
    const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' });
    const date = now.toLocaleDateString('nl-BE', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
    const timeOfDay = this.getTimeOfDay(hour);
    const sessionDuration = this.getSessionDuration();
    const timeSinceLastMessage = this.getTimeSinceLastMessage();
    
    let contextString = `[Time Context: ${dayOfWeek}, ${date}, ${hour}:${minutes.toString().padStart(2, '0')} (${timeOfDay})`;
    
    if (sessionDuration > 60) {
      contextString += ` | Session: ${Math.round(sessionDuration / 60)} min`;
    }
    
    if (timeSinceLastMessage && timeSinceLastMessage > 300) {
      contextString += ` | Gap: ${Math.round(timeSinceLastMessage / 60)} min since last message`;
    }
    
    contextString += ']';
    
    return contextString;
  }

  // ==========================================
  // SESSION TRACKING
  // ==========================================

  /**
   * Get session duration in seconds
   */
  getSessionDuration() {
    return Math.round((Date.now() - this.sessionStart) / 1000);
  }

  /**
   * Get formatted session duration
   */
  getFormattedSessionDuration() {
    const seconds = this.getSessionDuration();
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  }

  /**
   * Record message sent
   */
  recordMessage() {
    const now = Date.now();
    this.lastMessageTime = now;
    this.messageCount++;
    
    // Update patterns
    this.updatePatterns(now);
  }

  /**
   * Get time since last message in seconds
   */
  getTimeSinceLastMessage() {
    if (!this.lastMessageTime) return null;
    return Math.round((Date.now() - this.lastMessageTime) / 1000);
  }

  // ==========================================
  // PATTERN TRACKING (ADHD Foundation)
  // ==========================================

  /**
   * Load patterns from localStorage
   */
  loadPatterns() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.warn('[TimeAwareness] Could not load patterns:', e);
    }
    
    return {
      sessions: [],
      hourlyActivity: new Array(24).fill(0),
      dailyActivity: new Array(7).fill(0),
      averageSessionLength: 0,
      totalSessions: 0,
      lastUpdated: null
    };
  }

  /**
   * Save patterns to localStorage
   */
  savePatterns() {
    try {
      this.patterns.lastUpdated = Date.now();
      localStorage.setItem(this.storageKey, JSON.stringify(this.patterns));
    } catch (e) {
      console.warn('[TimeAwareness] Could not save patterns:', e);
    }
  }

  /**
   * Record session start
   */
  recordSessionStart() {
    const now = new Date();
    this.patterns.hourlyActivity[now.getHours()]++;
    this.patterns.dailyActivity[now.getDay()]++;
    this.savePatterns();
  }

  /**
   * Update patterns with message activity
   */
  updatePatterns(timestamp) {
    const date = new Date(timestamp);
    // Track hourly patterns
    this.patterns.hourlyActivity[date.getHours()]++;
    this.savePatterns();
  }

  /**
   * Record session end (call on app close/chat switch)
   */
  recordSessionEnd() {
    const duration = this.getSessionDuration();
    
    // Only record meaningful sessions (>30 seconds)
    if (duration > 30) {
      this.patterns.sessions.push({
        start: this.sessionStart,
        duration: duration,
        messageCount: this.messageCount,
        date: new Date(this.sessionStart).toISOString().split('T')[0]
      });
      
      // Keep only last 100 sessions
      if (this.patterns.sessions.length > 100) {
        this.patterns.sessions = this.patterns.sessions.slice(-100);
      }
      
      // Recalculate average
      const totalDuration = this.patterns.sessions.reduce((sum, s) => sum + s.duration, 0);
      this.patterns.averageSessionLength = Math.round(totalDuration / this.patterns.sessions.length);
      this.patterns.totalSessions = this.patterns.sessions.length;
      
      this.savePatterns();
    }
  }

  /**
   * Get pattern insights
   */
  getPatternInsights() {
    const patterns = this.patterns;
    
    // Find peak hours
    const peakHour = patterns.hourlyActivity.indexOf(Math.max(...patterns.hourlyActivity));
    
    // Find peak day
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const peakDay = days[patterns.dailyActivity.indexOf(Math.max(...patterns.dailyActivity))];
    
    return {
      peakHour: peakHour,
      peakHourFormatted: `${peakHour}:00`,
      peakDay: peakDay,
      averageSessionMinutes: Math.round(patterns.averageSessionLength / 60),
      totalSessions: patterns.totalSessions,
      hourlyDistribution: patterns.hourlyActivity,
      dailyDistribution: patterns.dailyActivity
    };
  }

  // ==========================================
  // SMART CONTEXT INJECTION
  // ==========================================

  /**
   * Should we inject time context?
   * (Avoids redundant context on rapid messages)
   */
  shouldInjectContext() {
    // Always inject on first message
    if (this.messageCount <= 1) return true;
    
    // Inject if significant time gap (>5 minutes)
    const gap = this.getTimeSinceLastMessage();
    if (gap && gap > 300) return true;
    
    // Inject every 10 messages
    if (this.messageCount % 10 === 0) return true;
    
    return false;
  }

  /**
   * Get context for AI prompt (only if needed)
   */
  getContextForPrompt() {
    if (!this.shouldInjectContext()) return null;
    return this.formatContextForPrompt(new Date());
  }

  // ==========================================
  // STATE DETECTION FOUNDATION
  // ==========================================

  /**
   * Detect potential state based on time patterns
   * (Foundation for ADHD state detection)
   */
  detectTimeBasedState() {
    const hour = new Date().getHours();
    const sessionDuration = this.getSessionDuration();
    const sessionMinutes = sessionDuration / 60;
    
    let indicators = [];
    
    // Late night + long session = possible hyperfocus/activation
    if ((hour >= 23 || hour < 4) && sessionMinutes > 60) {
      indicators.push('late_night_activation');
    }
    
    // Very long session (>3 hours) = possible hyperfocus
    if (sessionMinutes > 180) {
      indicators.push('extended_session');
    }
    
    // Rapid messages (high count in short time)
    if (this.messageCount > 20 && sessionMinutes < 30) {
      indicators.push('rapid_exchange');
    }
    
    // Long gap then sudden return
    const gap = this.getTimeSinceLastMessage();
    if (gap && gap > 3600) { // >1 hour gap
      indicators.push('return_after_break');
    }
    
    return {
      indicators: indicators,
      sessionMinutes: Math.round(sessionMinutes),
      messageCount: this.messageCount,
      hour: hour
    };
  }
}

// Singleton instance
const timeAwareness = new TimeAwareness();

// Record session end on page unload
window.addEventListener('beforeunload', () => {
  timeAwareness.recordSessionEnd();
});

export default timeAwareness;
